import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, getErrorMessage } from '../client';
import { queryKeys } from '../queryKeys';
import { calculateSkip, calculateTotalPages } from '@/lib/pagination';
import type { PaginationParams, PaginatedResponse } from '../types/common';
import type { RuleConditions } from '@/components/criteria-builder';

// Setting scope enum
export type SettingScope = 'GLOBAL' | 'WAREHOUSE' | 'ZONE' | 'ORDER_TYPE';

// Setting entity
export interface Setting {
  id: string;
  key: string;
  value: Record<string, unknown>;
  scope: SettingScope;
  scopeId: string | null;
  description: string | null;
  /**
   * Optional runtime activation criteria. When present, the setting only
   * applies when the resolve-time context matches. `null` (or absent) ⇒
   * unconditional resolution. See spec §IW-5.
   */
  criteria?: RuleConditions | null;
  createdAt: string;
  updatedAt: string;
}

// DTOs
export interface CreateSettingDto {
  key: string;
  value: Record<string, unknown>;
  scope?: SettingScope;
  scopeId?: string;
  description?: string;
  /**
   * Optional activation criteria. Omit (or send `null`) for legacy
   * unconditional behaviour. See spec §IW-5.
   */
  criteria?: RuleConditions;
}

export interface UpdateSettingDto {
  value?: Record<string, unknown>;
  description?: string;
  criteria?: RuleConditions | null;
}

// Query params
export interface SettingQueryParams extends PaginationParams {
  scope?: SettingScope;
  scopeId?: string;
  keyPattern?: string;
}

// Resolve setting params
export interface ResolveSettingParams {
  warehouseId?: string;
  zoneId?: string;
  orderType?: string;
  customerId?: string;
}

// API response
interface SettingApiResponse {
  settings: Setting[];
  total: number;
}

// Get all settings
export function useSettings(params?: SettingQueryParams) {
  return useQuery({
    queryKey: queryKeys.settings.list(params),
    queryFn: async () => {
      const queryParams: Record<string, string> = {};
      if (params?.page)
        queryParams.skip = String(
          calculateSkip(params.page, params.limit ?? 20),
        );
      if (params?.limit) queryParams.take = String(params.limit);
      if (params?.scope) queryParams.scope = params.scope;
      if (params?.scopeId) queryParams.scopeId = params.scopeId;
      if (params?.keyPattern) queryParams.keyPattern = params.keyPattern;

      const { data } = await apiClient.get<SettingApiResponse>(
        '/api/settings',
        {
          params: queryParams,
        },
      );

      return {
        data: data.settings,
        total: data.total,
        page: params?.page ?? 1,
        limit: params?.limit ?? 20,
        totalPages: calculateTotalPages(data.total, params?.limit ?? 20),
      } as PaginatedResponse<Setting>;
    },
    placeholderData: (previousData) => previousData,
  });
}

// Resolve setting value with scope hierarchy
export function useResolveSetting(
  key: string | undefined,
  scopes?: ResolveSettingParams,
) {
  return useQuery({
    queryKey: queryKeys.settings.resolve(key, scopes),
    queryFn: async () => {
      const queryParams: Record<string, string> = {};
      if (scopes?.warehouseId) queryParams.warehouseId = scopes.warehouseId;
      if (scopes?.zoneId) queryParams.zoneId = scopes.zoneId;
      if (scopes?.orderType) queryParams.orderType = scopes.orderType;
      if (scopes?.customerId) queryParams.customerId = scopes.customerId;

      const { data } = await apiClient.get<{
        value: Record<string, unknown> | null;
      }>(`/api/settings/resolve/${key}`, { params: queryParams });
      return data.value;
    },
    enabled: !!key,
  });
}

// Create setting
export function useCreateSetting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dto: CreateSettingDto) => {
      const { data } = await apiClient.post<Setting>('/api/settings', dto);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.all });
    },
    onError: (error) => {
      console.error('Failed to create setting:', getErrorMessage(error));
    },
  });
}

// Update setting
export function useUpdateSetting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, dto }: { id: string; dto: UpdateSettingDto }) => {
      const { data } = await apiClient.patch<Setting>(
        `/api/settings/${id}`,
        dto,
      );
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.all });
      queryClient.setQueryData(queryKeys.settings.detail(data.id), data);
    },
    onError: (error) => {
      console.error('Failed to update setting:', getErrorMessage(error));
    },
  });
}

// Delete setting
export function useDeleteSetting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.delete<Setting>(`/api/settings/${id}`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.settings.all });
    },
    onError: (error) => {
      console.error('Failed to delete setting:', getErrorMessage(error));
    },
  });
}

/**
 * Effective `short_picks.fill_flow_enabled` for ONE warehouse
 * (`GET /settings/effective/fill-flow`, short-pick plan §4 S4). Read by the
 * Sidebar (hides the interim /short-picks entry) and the /short-picks banner.
 * Any role with access to the warehouse may read it. Missing / failed ⇒ OFF.
 */
export function useFillFlow(warehouseId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.settings.fillFlow(warehouseId),
    queryFn: async () => {
      const { data } = await apiClient.get<{ enabled: boolean }>(
        '/api/settings/effective/fill-flow',
        { params: { warehouseId } },
      );
      return data;
    },
    enabled: !!warehouseId,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}
